/**
 * Dev bridge (O-026) — a narrow active-instance accessor so the React playtest
 * panel can reach the live `Game`'s client-local dungeon layer.
 *
 * Mirrors the readonly-singleton pattern quality.ts already uses
 * (`setActiveQualityProfile` / `activeQualityProfile`). Deliberately narrow: it
 * exposes ONE getter rather than the `Game` instance, so the panel cannot reach
 * into the renderer, the socket or the sim. Nothing here rides the wire.
 *
 * `Game` registers itself only when the dev surface is enabled (devFlags.ts
 * `devToolsEnabled()`), and clears on dispose — so a production build never
 * registers, and a reconnect/remount never leaves a stale layer behind.
 *
 * Design: docs/features/playtest-panel.md
 */

import type { CameraMove } from './cameraMove.js';
import type { DungeonWorldLayer } from './dungeon/layer.js';

declare global {
  interface Window {
    /**
     * Playtest teleport: re-login as a seeded character standing at a point of
     * interest. Registered by App in dev builds only. Also driven by
     * CloudMediaStudio's AD-216 capture-plan executor — do not remove.
     */
    __GC_DEVJUMP?: (email: string, password: string, character: string) => Promise<boolean>;
    /**
     * Playtest dungeon entry: generate and enter a client-local dungeon interior
     * at the named difficulty. Registered by App in dev builds only, alongside
     * `__GC_DEVJUMP` and under the same `devToolsEnabled()` gate.
     *
     * WHY IT IS ON `window` AND `dungeonLayer()` IS NOT (q-visual-lane-no-dungeon-route):
     * the visual lane drives the page over CDP and can therefore see ONLY
     * `window`. The bridge below is a module singleton, so the dungeon interior
     * — an entirely visual surface, and the one AD-300 exists for — was
     * unobservable by the lane while four dungeon-interior art batches sat
     * `[o]` PENDING-OBSERVATION waiting for exactly this. Resolves to `false`
     * rather than throwing when the layer is absent (`?dungeons` off, or no
     * Game mounted), so the caller reports a finding instead of an exception.
     *
     * `options` (S4 of q-dungeon-layout-generator-build): `cycle` re-rolls the
     * shrine at that generation cycle (a different, still deterministic seed) and
     * `at` stands the walker at `entrance` / `choke` / `rest` / `boss`. Either
     * one the plan cannot honour resolves `false`, never a stand-in. `present`
     * hides the debug banner and the capsule walker and walks slowly forward — the
     * mode capture footage uses, since the capsule must never be public-facing.
     */
    __GC_DEVDUNGEON?: (
      difficultyId: string,
      options?: { cycle?: number; at?: string; present?: boolean },
    ) => Promise<boolean>;
    /**
     * Capture camera move (q-capture-camera-channel-devcam-bridge): ease the
     * orbit boom — `{ yawBy?, pitchTo?, distTo?, seconds }` — over `seconds`.
     * The studio's `cam:` step is its caller; same gate and lifetime as the two
     * hooks above. Resolves `true` once the move has STARTED (the studio's own
     * schedule holds for its duration), `false` for an op cameraMove.ts refuses
     * — including any target outside CAMERA_BOUNDS — or when no Game is mounted.
     */
    __GC_DEVCAM?: (op: unknown) => Promise<boolean>;
  }
}

export interface DevBridge {
  /** The dungeon layer for the currently joined zone, or null when `?dungeons` is off. */
  readonly dungeonLayer: () => DungeonWorldLayer | null;
  /** Start a validated camera move on the live orbit camera; replaces any move in flight. */
  readonly cameraMove: (move: CameraMove) => boolean;
}

let active: DevBridge | null = null;

/** Install (or, with `null`, clear) the active bridge. Called by `Game` only. */
export function setDevBridge(bridge: DevBridge | null): void {
  active = bridge;
}

/** The active bridge, or null when no Game is mounted / this is a production build. */
export function devBridge(): DevBridge | null {
  return active;
}
